'use client';

import {
  Button,
  ButtonGroup,
  Dialog,
  DocumentViewer,
  Flex,
  PDFViewer,
  PDFControls,
  Text,
  XLSViewer,
} from '@/_components';
import { useContext, useState, type FC, type ReactNode } from 'react';
import type { PreviewDialogProps } from './PreviewDialog.types';
import { getExtensionOfFilename } from '@/_utils/files';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { downloadDocument } from '@/_api/client-documents';

export const PreviewDialog: FC<PreviewDialogProps> = () => {
  const {
    previewFile,
    setPreviewFile,
    previewFileOriginalLink,
    previewAnnotations,
    setPreviewAnnotations,
    setPreviewCompanyIdForAnnotations,
    setPageRotations,
    previewCompanyIdForAnnotations,
    previewExtractedDocumentId,
  } = useContext(PreviewContext);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);

  const extension: string = getExtensionOfFilename(previewFile);
  const isPDF: boolean = extension === 'pdf';

  const onResetPreviewFile = () => {
    setPageRotations({});
    setPreviewFile(undefined);

    setPreviewAnnotations([]);
    setPreviewCompanyIdForAnnotations(undefined);
  };

  const onPasswordRequest = async (): Promise<string> => {
    return globalThis.prompt('Enter password:') || '';
  };

  const getRenderer = (): ReactNode => {
    if (!previewFile) return null;

    switch (extension) {
      case 'pdf':
        return (
          <PDFViewer
            annotations={previewAnnotations}
            url={previewFile}
            onPassword={onPasswordRequest}
          />
        );
      case 'xlsx':
      case 'xls':
        return (
          <Dialog.Content>
            <XLSViewer url={previewFile} />
          </Dialog.Content>
        );
      case 'jpg':
      case 'jpeg':
      case 'avif':
      case 'gif':
      case 'heic':
      case 'png':
        return (
          <Dialog.Content>
            <DocumentViewer url={previewFile} />
          </Dialog.Content>
        );
      default:
        return (
          <Dialog.Content>
            <Flex padding={6} isCentered={true}>
              <Text type="small" color="secondary">
                File type not supported yet
              </Text>
            </Flex>
          </Dialog.Content>
        );
    }
  };

  const shouldShowAnnotationsDialog =
    preferences?.annotation_enabled &&
    isPDF &&
    typeof previewCompanyIdForAnnotations === 'number' &&
    previewFileOriginalLink;

  const handleDownload = () => {
    if (shouldShowAnnotationsDialog) {
      setIsDownloadDialogOpen(true);
    } else {
      globalThis.open(previewFile, '_blank');
    }
  };

  const handleDownloadWithoutAnnotations = () => {
    setIsDownloadDialogOpen(false);
    globalThis.open(previewFile, '_blank');
  };

  const handleDownloadWithAnnotations = async () => {
    if (
      !previewFileOriginalLink ||
      !token ||
      typeof previewCompanyIdForAnnotations !== 'number'
    ) {
      return;
    }

    setIsDownloadDialogOpen(false);

    try {
      const { document, message } = await downloadDocument({
        companyId: previewCompanyIdForAnnotations,
        link: previewFileOriginalLink,
        includeAnnotations: true,
        extractedDocumentId: previewExtractedDocumentId ?? undefined,
        token,
      });

      if (message) {
        addToast('success', message);
      }

      if (document?.data?.link) {
        globalThis.open(document.data.link, '_blank');
      }
    } catch (error) {
      console.error('Failed to download document with annotations:', error);
    }
  };

  const dialogContent: ReactNode = getRenderer();

  return (
    <>
      <Dialog
        isOpen={!!previewFile}
        onClose={onResetPreviewFile}
        isWide={isPDF}
        isOptionalFullScreen={isPDF}
        title="Preview"
        headerButtons={
          <>
            {isPDF && <PDFControls />}
            <Button
              value="Download"
              size="small"
              isIconOnly={true}
              intent="white-fill"
              onClick={handleDownload}
              withTooltip
              tooltipPlacement="bottom"
              tooltip="Download"
              iconLeft="download"
            />
          </>
        }
      >
        {dialogContent}
      </Dialog>

      <Dialog
        isOpen={isDownloadDialogOpen}
        onClose={() => setIsDownloadDialogOpen(false)}
        title="Download options"
      >
        <Dialog.Content>
          <Text>Choose how you want to download this document:</Text>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              onClick={() => setIsDownloadDialogOpen(false)}
              value="Cancel"
            />
            <Button
              intent="secondary"
              onClick={handleDownloadWithoutAnnotations}
              value="Download without annotations"
            />
            <Button
              intent="primary"
              onClick={handleDownloadWithAnnotations}
              value="Download with annotations"
              isDisabled={previewAnnotations.length === 0}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
    </>
  );
};
